//#region import libraries
import React, { useEffect, useState } from "react";
// drop down menu
import Select from "react-select";
import { Form } from "react-bootstrap";
//redux
import { useDispatch, useSelector } from "react-redux";
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";
import {
    selectTimelineAppSettings,
    selectTimelineAppSettingsStatus,
    fetchTimelineAppSettings,
    updateTimelineAppSettings,
} from "_redux/slices/timeline/timeline-control";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { timelineRoles } from "../timeline-constants";

// Raw, unfiltered airport locations list. `selectAirportLocations` in
// timeline-areas narrows to the currently selected area/timeline elsewhere in
// the portal — this settings panel needs every location regardless of that.
const selectAllAirportLocations = (state) => state.timelineAreas.airportLocations || [];

// Bordered container that visually ties a set of related controls together,
// so the panel reads as groups rather than a flat stack of unrelated rows.
const SettingsGroup = ({ title, usedTheme, children }) => (
    <div
        style={{
            border: `1px solid ${usedTheme.BORDER_COLOR}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            backgroundColor: usedTheme.TIMELINE_COLOR,
        }}
    >
        <div
            style={{
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: usedTheme.TEXT_COLOR,
                opacity: 0.6,
                marginBottom: '12px',
            }}
        >
            {title}
        </div>
        {children}
    </div>
);

// constants
const viewAppTasksOptions = [
    { value: "1", label: "01" },
    { value: "2", label: "02" },
    { value: "5", label: "05" },
    { value: "10", label: "10" },
    { value: "all", label: "ALL" },
]
//#endregion

const TimelineAppSettings = () => {

    //#region redux
    const dispatch = useDispatch()
    const usedTheme = useSelector(selectUsedTheme);
    const dbUser = useSelector(selectFirebaseInfo);
    const timelineAppSettings = useSelector(selectTimelineAppSettings);
    const timelineAppSettingsStatus = useSelector(selectTimelineAppSettingsStatus);
    const firebaseUser = useSelector(selectFirebaseUser);
    const airportLocations = useSelector(selectAllAirportLocations);

    const iata = dbUser?.details?.authority;

    // The QR shift and break queue toggles are admin-only (same gate used across the portal).
    const isAdmin = dbUser?.role === "ADMIN" || dbUser?.role === "GARETH";

    //#endregion

    const [selfAllocationAccess, setSelfAllocationAccess] = useState([]);
    const [viewAppTasks, setViewAppTasks] = useState(null);
    const [allowAddPax, setAllowAddPax] = useState([]);
    const [allTasksVisibleRoles, setAllTasksVisibleRoles] = useState([]);
    // Defaults to true for Dublin until an explicit value is stored.
    const [enableLocationQRShift, setEnableLocationQRShift] = useState(false);
    // Roles allowed to start/end a shift from anywhere (skip the QR scan).
    const [locationQRExemptRoles, setLocationQRExemptRoles] = useState([]);
    // Location _ids staff are allowed to scan to start/end a shift. Kept as
    // plain ids (not {value,label} pairs) since airportLocations loads async
    // and shouldn't gate hydrating this from saved settings.
    const [qrShiftLocationIds, setQrShiftLocationIds] = useState([]);
    // Hold tasks assigned to an agent on break until the break is finished (prm-timeline utils/breakQueue.js).
    const [queueTasksDuringBreak, setQueueTasksDuringBreak] = useState(false);

    useEffect(() => {
        (async () => {
            if (!iata) {
                return;
            }
            const response = await dispatch(fetchTimelineAppSettings(iata));
            if (response?.meta?.requestStatus === "fulfilled") {
                const tempSettings = response?.payload?.settings;
                setSelfAllocationAccess(tempSettings?.selfAllocationAccess?.map(role => formatSelectValue(role)));
                setViewAppTasks(viewAppTasksOptions?.find(option => option?.value === tempSettings?.viewAppTasks) ?? null);
                setAllowAddPax(tempSettings?.allowAddPax?.map(role => formatSelectValue(role)));
                setAllTasksVisibleRoles(Array?.isFullArray(tempSettings?.allTasksVisibleRoles) ? tempSettings?.allTasksVisibleRoles?.map(role => formatSelectValue(role)) : []);
                setEnableLocationQRShift(tempSettings?.enableLocationQRShift ?? (iata === "DUB"));
                setLocationQRExemptRoles(Array?.isFullArray(tempSettings?.locationQRExemptRoles) ? tempSettings?.locationQRExemptRoles?.map(role => formatSelectValue(role)) : []);
                setQrShiftLocationIds(Array?.isFullArray(tempSettings?.qrShiftLocationIds) ? tempSettings?.qrShiftLocationIds : []);
                setQueueTasksDuringBreak(tempSettings?.queueTasksDuringBreak === true);
            }
        })();
    }, [])

    const handleUpdateTimelineAppSettings = async (body) => {
        const response = await dispatch(updateTimelineAppSettings({
            iata: iata,
            body: {
                settings: body,
                userId: firebaseUser?.uid,
            }
        }));
        if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(fetchTimelineAppSettings(iata));
        }
    };

    const formatSelectValue = (id) => {

        let tempRole = timelineRoles?.find(role => role?.value == id);
        if (tempRole) {
            return {
                value: tempRole?.value,
                label: tempRole?.label
            }
        } else {
            return null;
        }
    }

    const getSelectStyles = () => ({

        control: (base) => ({
            ...base,
            minWidth: "200px",
            borderRadius: "5px",
            zIndex: 3,
            backgroundColor: usedTheme.BACKGROUND_COLOR,
            borderColor: usedTheme.BORDER_COLOR,
            color: usedTheme.TEXT_COLOR,
        }),
        placeholder: (provided) => ({
            ...provided,
            color: usedTheme.TEXT_COLOR,
        }),
        singleValue: (provided) => ({
            ...provided,
            color: usedTheme.TEXT_COLOR,
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 4,
            backgroundColor: usedTheme.BACKGROUND_COLOR,
            borderColor: usedTheme.BORDER_COLOR,
            color: usedTheme.TEXT_COLOR
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused
                ? usedTheme.BORDER_COLOR
                : usedTheme.BACKGROUND_COLOR,
            color: usedTheme.TEXT_COLOR,
            cursor: 'pointer',
        }),

    })

    const qrShiftLocationOptions = airportLocations.map(location => ({ value: location._id, label: location.title }));
    const qrShiftLocationValue = qrShiftLocationOptions.filter(option => qrShiftLocationIds.includes(option.value));

    return (
        <div>

            {isAdmin && (
                <SettingsGroup title="Shift QR Verification" usedTheme={usedTheme}>
                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: (enableLocationQRShift ? '16px' : 0) }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                                Enable Location QR Shift
                            </span>
                            <Form.Check
                                type="switch"
                                id="enable-location-qr-shift"
                                checked={enableLocationQRShift}
                                onChange={(e) => {
                                    const value = e.target.checked;
                                    setEnableLocationQRShift(value);
                                    handleUpdateTimelineAppSettings({ enableLocationQRShift: value });
                                }}
                            />
                        </div>
                        <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                            Require employees to scan a location QR code when starting or ending a shift
                        </span>
                    </div>

                    {enableLocationQRShift && (
                        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                            <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                                Location QR Exempt Roles
                            </span>
                            <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px', marginBottom: '8px' }}>
                                Roles allowed to start/end a shift from anywhere (no QR scan). Leave empty to require the QR scan for everyone.
                            </span>
                            <Select
                                value={locationQRExemptRoles}
                                onChange={(selectedOption) => {
                                    setLocationQRExemptRoles(selectedOption);
                                }}
                                options={timelineRoles?.map(role => ({ value: role.value, label: role.label }))}
                                placeholder="Choose a Role..."
                                styles={getSelectStyles()}
                                isClearable
                                isMulti={true}
                                closeMenuOnSelect={false}
                                onMenuClose={() => {
                                    handleUpdateTimelineAppSettings({
                                        locationQRExemptRoles: Array?.isFullArray(locationQRExemptRoles)
                                            ? locationQRExemptRoles?.map(role => role?.value)
                                            : []
                                    });
                                }}
                            />
                        </div>
                    )}

                    {enableLocationQRShift && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                                QR Shift Locations
                            </span>
                            <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px', marginBottom: '8px' }}>
                                Locations staff can scan to start/end a shift. Leave empty to allow any location QR code.
                            </span>
                            <Select
                                value={qrShiftLocationValue}
                                onChange={(selectedOption) => {
                                    setQrShiftLocationIds(Array?.isFullArray(selectedOption) ? selectedOption.map(option => option.value) : []);
                                }}
                                options={qrShiftLocationOptions}
                                placeholder="Search locations..."
                                styles={getSelectStyles()}
                                isClearable
                                isMulti={true}
                                closeMenuOnSelect={false}
                                onMenuClose={() => {
                                    handleUpdateTimelineAppSettings({ qrShiftLocationIds });
                                }}
                            />
                        </div>
                    )}
                </SettingsGroup>
            )}

            {isAdmin && (
                <SettingsGroup title="Breaks" usedTheme={usedTheme}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                                Queue Tasks During Break
                            </span>
                            <Form.Check
                                type="switch"
                                id="queue-tasks-during-break"
                                checked={queueTasksDuringBreak}
                                onChange={(e) => {
                                    const value = e.target.checked;
                                    setQueueTasksDuringBreak(value);
                                    handleUpdateTimelineAppSettings({ queueTasksDuringBreak: value });
                                }}
                            />
                        </div>
                        <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                            Tasks assigned to an employee on break stay off their device until the break is finished
                        </span>
                    </div>
                </SettingsGroup>
            )}

            <SettingsGroup title="App & Task Settings" usedTheme={usedTheme}>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                            View App Tasks
                        </span>
                        <Select
                            value={viewAppTasks}
                            onChange={(selectedOption) => {
                                setViewAppTasks(selectedOption);
                                handleUpdateTimelineAppSettings({
                                    viewAppTasks: selectedOption?.value ?? "all"
                                }
                                );

                            }}
                            options={viewAppTasksOptions}
                            placeholder="Choose a Number..."
                            styles={getSelectStyles()}
                        />
                    </div>
                    <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                        Select the number of tasks displayed for the employee in the app
                    </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                            All tasks visible roles
                        </span>
                        <Select
                            value={allTasksVisibleRoles}
                            onChange={(selectedOption) => {
                                setAllTasksVisibleRoles(selectedOption);
                            }}
                            isMulti={true}
                            closeMenuOnSelect={false}
                            onMenuClose={() => {
                                handleUpdateTimelineAppSettings({
                                    allTasksVisibleRoles: Array?.isFullArray(allTasksVisibleRoles)
                                        ? allTasksVisibleRoles?.map(role => role?.value)
                                        : []
                                }
                                );
                            }}
                            options={timelineRoles?.map(role => ({ value: role.value, label: role.label }))}
                            placeholder="Choose a Role..."
                            styles={getSelectStyles()}
                        />
                    </div>
                    <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                        Select the roles that are allowed to view all tasks in the app
                    </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                            Self Allocation access
                        </span>
                        <Select
                            value={selfAllocationAccess}
                            onChange={(selectedOption) => {
                                setSelfAllocationAccess(selectedOption)
                            }}
                            options={timelineRoles?.map(role => ({ value: role.value, label: role.label }))}
                            placeholder="Choose a Role..."
                            styles={getSelectStyles()}
                            isClearable
                            isMulti={true}
                            onMenuClose={() => {
                                handleUpdateTimelineAppSettings({
                                    selfAllocationAccess: Array?.isFullArray(selfAllocationAccess)
                                        ? selfAllocationAccess?.map(role => role?.value)
                                        : []
                                }
                                );

                            }}
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                        Select the self allocation access for the employee roles
                    </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', color: 'gray', fontWeight: '500' }}>
                            Allow Add Pax
                        </span>
                        <Select
                            value={allowAddPax}
                            onChange={(selectedOption) => {
                                setAllowAddPax(selectedOption)
                            }}
                            options={timelineRoles?.map(role => ({ value: role.value, label: role.label }))}
                            placeholder="Choose a Role..."
                            styles={getSelectStyles()}
                            isClearable
                            isMulti={true}
                            onMenuClose={() => {
                                handleUpdateTimelineAppSettings({
                                    allowAddPax: Array?.isFullArray(allowAddPax)
                                        ? allowAddPax?.map(role => role?.value)
                                        : []
                                }
                                );

                            }}
                            closeMenuOnSelect={false}
                        />
                    </div>
                    <span style={{ color: usedTheme.TEXT_COLOR, fontSize: '14px', marginTop: '4px' }}>
                        Select the roles that are allowed to add pax
                    </span>
                </div>
            </SettingsGroup>
        </div>
    )
}

export default TimelineAppSettings
